import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether the observed element is narrower than `threshold` CSS pixels.
 * Measures the actual content-area width (not the viewport), so it ignores the fixed
 * sidebar/chrome and naturally responds to browser zoom (zoom shrinks CSS px). Used to
 * switch Hero dashboard widgets to their compact/dense presentation on narrow or
 * high-DPI screens.
 *
 * Attach the returned `ref` to the container whose inner width should drive the layout
 * (i.e. the dashboard content wrapper, not the page chrome).
 */
export const useCompactWidth = (threshold = 900) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width ?? 0;
            setCompact(w > 0 && w < threshold);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [threshold]);
    return { ref, compact };
};

export default useCompactWidth;
